import { useCallback, useState } from "react";

import { useOrderMutation, useOrdersCache, useS3Mutation } from "@web-app/api";
import { Button, Icon, Skeleton, useAlert } from "@web-app/components";
import { Attachment, Order, OrderDeliveryType, User } from "@web-app/types";

interface OrderAttachmentsTabProps {
  order: Order;
}

const EmptyState = () => {
  return (
    <div className="flex flex-col items-center justify-center py-8 md:py-12 px-4 text-center w-full border border-gray-200 rounded-md">
      <div className="mb-4">
        <Icon name="fileImage" className="h-8 md:h-12 text-gray-300" />
      </div>
      <p className="text-xs md:text-sm text-gray-500">
        Uploaded attachments will be displayed here.
      </p>
    </div>
  );
};

const AttachmentPreview = ({ attachment }: { attachment: Attachment }) => {
  const [imageLoadState, setImageLoadState] = useState<
    "error" | "loaded" | "loading"
  >("loading");

  return (
    <div className="relative flex h-52 w-52 items-center justify-center overflow-hidden rounded-md border border-gray-200 bg-gray-50 p-2 md:h-64 md:w-64">
      {imageLoadState === "loading" && (
        <Skeleton height="h-48 md:h-60" width="w-full" text="Loading..." />
      )}
      <img
        src={attachment.url}
        alt="Order attachment"
        className={
          imageLoadState === "loaded"
            ? "max-h-48 max-w-full object-contain md:max-h-60"
            : "hidden"
        }
        onError={() => setImageLoadState("error")}
        onLoad={() => setImageLoadState("loaded")}
      />
      {imageLoadState === "error" && (
        <span className="text-sm text-gray-500">
          Unable to load this attachment.
        </span>
      )}
      <a
        href={attachment.url}
        target="_blank"
        rel="noopener noreferrer"
        className="absolute top-2 right-2 z-10 inline-flex h-6 w-6 items-center justify-center rounded-md bg-white text-xs text-gray-700 shadow-md hover:bg-gray-50"
        aria-label="Download attachment"
        title="Download attachment"
      >
        <Icon name="download" className="h-4 w-4" />
      </a>
    </div>
  );
};

const AttachmentList = ({
  attachments,
  handleAttachmentsChange,
  title,
  uploadId,
}: {
  attachments: Attachment[];
  handleAttachmentsChange: (value: File[]) => void;
  title: string;
  uploadId: string;
}) => {
  return (
    <>
      <div className="flex justify-between py-2 items-center">
        <h3 className="text-sm md::text-md font-medium text-gray-900">
          {title}
        </h3>

        <Button htmlFor={uploadId} color="light" size="sm" icon="plus">
          Add Attachments
        </Button>
        <input
          hidden
          id={uploadId}
          type="file"
          accept="image/png, image/jpeg"
          multiple
          onChange={(e) => {
            handleAttachmentsChange(Array.from(e.target.files!));
            e.target.value = "";
          }}
        />
      </div>
      {attachments.length == 0 ? (
        <EmptyState />
      ) : (
        <div className="flex flex-wrap gap-3 rounded-md border border-gray-200 p-3">
          {attachments.map((attachment) => (
            <AttachmentPreview key={attachment.code} attachment={attachment} />
          ))}
        </div>
      )}
    </>
  );
};

export default function OrderAttachmentsTab({
  order,
}: OrderAttachmentsTabProps) {
  const { updateOrder } = useOrderMutation();
  const ordersCache = useOrdersCache();
  const { showAlertAsync, showAlert } = useAlert();
  const { upload } = useS3Mutation();

  const pickupAttachments =
    order.attachments?.filter(
      (a) => a.type == "POP_IMAGE" || a.type == "PICKUP_SIGNATURE",
    ) ?? [];
  const dropoffAttachments =
    order.attachments?.filter(
      (a) => a.type == "POD_IMAGE" || a.type == "SIGNATURE",
    ) ?? [];

  const handleAttachmentsChange = useCallback(
    async (files: File[], type: string) => {
      const payload = {} as Partial<Order>;
      if (files.length > 4) {
        showAlert({
          title: "You can only upload up to 4 files!",
          type: "error",
        });
        return;
      }
      const maxFileSize = 5 * 1024 * 1024;
      if (files.some((file) => file.size > maxFileSize)) {
        showAlert({
          title: "Some files are larger than 5MB!",
          type: "error",
        });
        return;
      }

      const uploadedUrls = await showAlertAsync(
        Promise.all(
          files.map(async (file) => {
            return upload.mutateAsync(file);
          }),
        ),
        {
          error: {
            title: "Something went wrong, please try again!",
            type: "error",
          },
          loading: { title: "Uploading images ...", type: "loading" },
          success: {
            title: "Successfully Uploaded Images",
            type: "success",
          },
        },
      );

      if (!uploadedUrls || uploadedUrls.length === 0) {
        return;
      }

      ordersCache.updateCachedOrders((data) => {
        const orders = data?.orders;
        const assignees = data?.assignees;

        if (orders) {
          const clonedOrders = structuredClone(orders);
          const clonedOrder = clonedOrders.get(order.id);

          if (!clonedOrder) {
            return data;
          }

          const existingAttachments = clonedOrder.attachments ?? [];

          const newAttachments =
            uploadedUrls?.map((url) => ({
              code: crypto.randomUUID(),
              type: type,
              url: url,
            })) ?? [];

          clonedOrder.attachments = [...existingAttachments, ...newAttachments];
          payload.attachments = [...existingAttachments, ...newAttachments];

          clonedOrders.set(order.id, clonedOrder);
          return {
            apps: data.apps,
            assignees: assignees || new Map<string, User>(),
            orders: clonedOrders,
          };
        }
        return data;
      });

      await showAlertAsync(
        updateOrder.mutateAsync({
          ...payload,
          id: order.id,
        }),
        {
          error: {
            title: "Something went wrong, please try again!",
            type: "error",
          },
          loading: { title: "Updating Order ...", type: "loading" },
          success: {
            title: "Successfully Updated Order",
            type: "success",
          },
        },
      );
    },
    [order, ordersCache, showAlert, showAlertAsync, updateOrder, upload],
  );

  return (
    <div className="px-4">
      {order.type == OrderDeliveryType.INDIVIDUAL_PICKUP_AND_DROPOFF && (
        <>
          <AttachmentList
            attachments={pickupAttachments}
            title="Pick Up"
            uploadId="pickup-attachment-upload"
            handleAttachmentsChange={(files) =>
              handleAttachmentsChange(files, "POP_IMAGE")
            }
          />
          <br />
          <AttachmentList
            attachments={dropoffAttachments}
            title="Drop Off"
            uploadId="dropoff-attachment-upload"
            handleAttachmentsChange={(files) =>
              handleAttachmentsChange(files, "POD_IMAGE")
            }
          />
        </>
      )}
      {order.type == OrderDeliveryType.ROUTE_OPT_PICKUP && (
        <>
          <AttachmentList
            attachments={pickupAttachments}
            title="Pick Up"
            uploadId="pickup-attachment-upload"
            handleAttachmentsChange={(files) =>
              handleAttachmentsChange(files, "POP_IMAGE")
            }
          />
        </>
      )}
      {order.type == OrderDeliveryType.ROUTE_OPT_DROPOFF && (
        <>
          <AttachmentList
            attachments={dropoffAttachments}
            title="Drop Off"
            uploadId="dropoff-attachment-upload"
            handleAttachmentsChange={(files) =>
              handleAttachmentsChange(files, "POD_IMAGE")
            }
          />
        </>
      )}
    </div>
  );
}
